/**
 * Superuser-only user management. Lists every Lighthouse user, shows
 * their role + access grants, and lets the superuser change them.
 */
import { useEffect, useState } from "react";
import { api, useAuth } from "./auth";
import { EstateSummary, listEstates as fetchEstates } from "./api";

type User = {
  profile: { email: string; role?: "user" | "superuser"; updated_at?: string };
  access: { sk: string; scope: string }[];
};

export function Users() {
  const { me } = useAuth();
  const [users, setUsers] = useState<User[] | null>(null);
  const [estates, setEstates] = useState<EstateSummary[]>([]);
  const [err, setErr] = useState<string | null>(null);
  const [editing, setEditing] = useState<string | null>(null);

  useEffect(() => {
    api<{ users: User[] }>("/users").then((d) => setUsers(d.users)).catch((e) => setErr(e.message));
    fetchEstates().then(setEstates).catch(() => {});
  }, []);

  if (!me?.is_superuser) {
    return <div className="empty">Superusers only.</div>;
  }

  return (
    <>
      <h1>Users</h1>
      <p className="subtitle">{users ? `${users.length} ${users.length === 1 ? "user" : "users"}` : "Loading…"}</p>

      {err && <div className="empty" style={{ color: "var(--down)" }}>{err}</div>}

      {users && (
        <div className="service-table" style={{ overflow: "visible" }}>
          {users.map((u) => (
            <div key={u.profile.email} style={{ borderTop: "1px solid var(--border)", padding: 16 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{u.profile.email}</div>
                  <div style={{ color: "var(--text-2)", fontSize: 12, marginTop: 2 }}>
                    {u.profile.role === "superuser" ? "Superuser · sees everything" : `${u.access.length} access grant${u.access.length === 1 ? "" : "s"}`}
                  </div>
                </div>
                <button
                  onClick={() => setEditing(editing === u.profile.email ? null : u.profile.email)}
                  style={{
                    border: "1px solid var(--border)",
                    padding: "6px 14px",
                    borderRadius: 8,
                    fontSize: 12,
                    color: "var(--text-1)",
                  }}
                >
                  {editing === u.profile.email ? "Cancel" : "Edit"}
                </button>
              </div>

              {editing === u.profile.email && (
                <EditUser
                  user={u}
                  estates={estates}
                  onSaved={async () => {
                    setEditing(null);
                    const d = await api<{ users: User[] }>("/users");
                    setUsers(d.users);
                  }}
                />
              )}
            </div>
          ))}
        </div>
      )}
    </>
  );
}

function EditUser({
  user,
  estates,
  onSaved,
}: {
  user: User;
  estates: EstateSummary[];
  onSaved: () => void;
}) {
  // Snapshot the user's current access into a Set we can toggle.
  const initialGrants = new Set(user.access.map((a) => a.scope));
  const [role, setRole] = useState<"user" | "superuser">(user.profile.role === "superuser" ? "superuser" : "user");
  const [grants, setGrants] = useState<Set<string>>(initialGrants);
  const [saving, setSaving] = useState(false);

  const toggle = (key: string) => {
    const next = new Set(grants);
    next.has(key) ? next.delete(key) : next.add(key);
    setGrants(next);
  };

  const save = async () => {
    setSaving(true);
    const access = Array.from(grants).map((k) => {
      // Stored format: ESTATE#<id> or ESTATE#<id>#UNIT#<u>
      const parts = k.split("#");
      return parts.includes("UNIT")
        ? { estate_id: parts[1], unit_id: parts[3] }
        : { estate_id: parts[1] };
    });
    await api(`/users/${encodeURIComponent(user.profile.email)}`, {
      method: "PUT",
      headers: { "content-type": "application/json" },
      body: JSON.stringify({ role, access }),
    });
    setSaving(false);
    onSaved();
  };

  return (
    <div style={{ marginTop: 16, paddingTop: 16, borderTop: "1px solid var(--border)" }}>
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontSize: 12, color: "var(--text-2)", textTransform: "uppercase", letterSpacing: "0.14em" }}>
          Role
        </label>
        <div style={{ marginTop: 6 }}>
          <select
            value={role}
            onChange={(e) => setRole(e.target.value as any)}
            style={{
              background: "var(--bg-0)",
              color: "var(--text-0)",
              padding: "8px 12px",
              borderRadius: 8,
              border: "1px solid var(--border)",
            }}
          >
            <option value="user">User</option>
            <option value="superuser">Superuser</option>
          </select>
        </div>
      </div>

      {role === "user" && (
        <div>
          <label style={{ fontSize: 12, color: "var(--text-2)", textTransform: "uppercase", letterSpacing: "0.14em" }}>
            Access
          </label>
          <div style={{ marginTop: 8, display: "grid", gap: 16 }}>
            {estates.map((e) => {
              const fullKey = `ESTATE#${e.id}`;
              const fullChecked = grants.has(fullKey);
              const units = ((e as any).units as { id: string; name: string }[]) || [];
              return (
                <div key={e.id} style={{ border: "1px solid var(--border)", borderRadius: 10, padding: 12 }}>
                  <label style={{ display: "flex", alignItems: "center", gap: 10, fontWeight: 600 }}>
                    <input type="checkbox" checked={fullChecked} onChange={() => toggle(fullKey)} />
                    {e.profile?.name || e.id}
                    <span style={{ color: "var(--text-2)", fontWeight: 400, fontSize: 12 }}>(whole estate)</span>
                  </label>
                  {!fullChecked && units.length > 0 && (
                    <div style={{ marginTop: 8, marginLeft: 24, display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 6 }}>
                      {units.map((u) => {
                        const k = `${fullKey}#UNIT#${u.id}`;
                        return (
                          <label key={u.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13 }}>
                            <input type="checkbox" checked={grants.has(k)} onChange={() => toggle(k)} />
                            {u.name}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      <button
        onClick={save}
        disabled={saving}
        style={{
          marginTop: 18,
          background: "var(--beacon)",
          color: "var(--bg-0)",
          padding: "10px 22px",
          borderRadius: 999,
          fontWeight: 600,
        }}
      >
        {saving ? "Saving…" : "Save"}
      </button>
    </div>
  );
}
